
import vModal from "@/components/vModal.vue";
import {Component, Model, Prop, Vue, Watch} from "vue-property-decorator";
import {ExportLabelState} from "@/models/NutritionLabels/ExportLabelState";
import {mapState} from "vuex";
import {Recipe} from "@/models/Recipes/Recipe";
import {DownloadNutritionLabelContentTypes} from "@/utils/API/RequestContentTypes";
import {LabelTypeMetadata} from "@/models/NutritionLabels/LabelTypeMetadata";
import UserRepository from "@/utils/API/UserRepository";
import {SET_USER_DETAILS} from "@/store/MutationTypes";
import NutritionLabelRepository from "@/utils/API/NutritionLabelRepository";
import {CanadaFOPHighInMetadata} from "@/models/NutritionLabels/CanadaFOPHighInMetadata";
import {RecipeCompositionMeasurement} from "@/models/Recipes/RecipeCompositionMeasurement";
import {dateDisplayFormat} from "@/utils/Defaults";
import {AdditionalLabelInformation} from "@/models/NutritionLabels/AdditionalLabelInformation";
import {ExportLabelView} from "@/models/Cautionary/ExportLabelView";
import {CautionaryFoodType} from "@/models/Cautionary/CautionaryFoodType";
import {CaffeinatedProductType} from "@/models/Cautionary/CaffeinatedProductType";
import {PaginatedViewManager} from "@/utils/PaginatedViewManager";
import { NutritionLabelCountry } from "@/models/NutritionLabels/NutritionLabelCountry";

@Component({
  components: {
    vModal,
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountStoreState: (state: any) => state.AccountModule,
    }),
  },
})
export default class vExportLabelButtons extends Vue {
  @Model() exportLabelState!: ExportLabelState;
  @Prop() packageLabelType!: LabelTypeMetadata;
  @Prop() isFopDisplay!: boolean;
  @Prop() activeView!: ExportLabelView;
  @Prop() recipe!: Recipe;
  @Prop() highIn!: CanadaFOPHighInMetadata;
  @Prop() subrecipeMeasurements!: RecipeCompositionMeasurement[];

  accountStoreState: any;
  localizationState: any;
  showLearnMoreModal = false;
  DATE_DISP_FORMAT = dateDisplayFormat;

  LabelTypeMetadata = LabelTypeMetadata;

  viewManager: PaginatedViewManager = new PaginatedViewManager([
    { order: 1, key: ExportLabelView.BOP, onEntry: (fro: number) => this.bopScreenEntry(fro) },
    {
      order: 2,
      key: ExportLabelView.FOP,
      predicate: this.hasNutritionalFrontOfPack,
      onEntry: (fro, to) => this.fopScreenEntry(fro, to),
      onExit: () => this.fopScreenExit()
    },
    { order: 3, key: ExportLabelView.CautionaryBOP, predicate: this.hasCautionaryLabels },
    {
      order: 4,
      key: ExportLabelView.CautionaryFOP,
      predicate: this.hasCautionaryLabels,
      onEntry: this.showSaveAndDownloadButtons.bind(this),
      onExit: this.hideSaveAndDownloadButtons.bind(this)
    }
  ]);

  shouldShowSaveAndDownloadButtons: boolean = false;

  created() {
    console.log("Running created hooks");
    this.viewManager.init();
  }

  showSaveAndDownloadButtons() {
    this.shouldShowSaveAndDownloadButtons = true;
  }

  hideSaveAndDownloadButtons() {
    this.shouldShowSaveAndDownloadButtons = false;
  }

  @Watch("exportLabelState.labelPreview")
  onLabelLoaded() {
    if(this.exportLabelState.nutritionCountry.id == NutritionLabelCountry.Canada){
      this.bopScreenEntry(null);
    }
  }

  bopScreenEntry(fromPage: number) {
    if (fromPage === null) {
      if (this.hasNutritionalFrontOfPack() || this.hasCautionaryLabels()) {
        this.hideSaveAndDownloadButtons();
        return;
      }

      this.showSaveAndDownloadButtons();
    }
  }

  fopScreenEntry(fromPage: number, toPage: number) {
    // Moving from BoP
    if (fromPage < toPage && !this.hasCautionaryLabels()) {
      this.showSaveAndDownloadButtons();
    }
  }

  fopScreenExit() {
    // Moving to Cautionary FoP
    this.hideSaveAndDownloadButtons();
  }

  hasNutritionalFrontOfPack() {
    return this.packageLabelType === LabelTypeMetadata.FrontOfPack;
  }

  hasCautionaryLabels(): boolean {
    return this.exportLabelState.hasValidCautionary;
  }

  get translations() {
    return this.localizationState.translations;
  }

  get downloadingMultipleLabels() {
    return this.viewManager.pageCount > 1;
  }

  get remainingLabelDownloads() {
    let labelsCount = 0;
    if (this.accountStoreState.userDetails.nutritionLabelsLimit.labelsLimit != null) {
      labelsCount =
        parseInt(this.accountStoreState.userDetails.nutritionLabelsLimit.labelsLimit) -
        parseInt(this.accountStoreState.userDetails.nutritionLabelsLimit.downloadedLabels);
    }

    if (labelsCount < 0) {
      labelsCount = 0;
    }

    return labelsCount;
  }

  get isCanada() {
    return this.exportLabelState.nutritionCountry.id === 0;
  }

  get isUSorUK() {
    const countryId = this.exportLabelState.nutritionCountry.id;
    return countryId === 1 || countryId === 2;
  }

  shouldShowPrevButton() {
    if (this.isCanada) {
      return !this.viewManager.isFirstPage;
    }

    else if (this.isUSorUK) {
      return false;
    }
  }

  @Watch("viewManager.currentPageKey")
  onNutrientsList(val: ExportLabelView) {
    this.$emit('switch-view', val);
  }

  shouldShowContinueButton() {
    if (this.isCanada) {
      return !this.viewManager.isLastPage;
    }

    return false; // Not shown for US/UK
  }

  // @Watch("exportLabelState.nutritionLabelType")
  // disableButtonsOnInvalidCautionary(newState: LabelTypeMetadata) {
  //   if (newState == LabelTypeMetadata.SupplementFacts) {
  //     this.hideSaveAndDownloadButtons();
  //   }
  // }

  @Watch("exportLabelState.hasValidCautionary")
  handleCautionaryValidity(newState: boolean) {
    if (newState || this.hasNutritionalFrontOfPack()) {
      this.hideSaveAndDownloadButtons();
    }

    else {
      this.showSaveAndDownloadButtons();
    }
  }

  get isInvalidSupplementFactsData() {
    const cautionarySoPData = this.exportLabelState.labelTypes[LabelTypeMetadata.CautionarySideOfPack];

    return (
      this.isCanada
      && this.exportLabelState.nutritionLabelType == LabelTypeMetadata.SupplementFacts
      // Cautionary food type is not selected
      && (!cautionarySoPData.foodType
        // The food type is caffeinated, but the caffeinated type is not selected
        || (cautionarySoPData.foodType == CautionaryFoodType.CaffeinatedSupplementedFood && !cautionarySoPData.caffeinatedType)
        // The food type is caffeinated, the caffeinated food type is chewing gum, but the quantity has not been provided
        || (cautionarySoPData.foodType == CautionaryFoodType.CaffeinatedSupplementedFood
          && cautionarySoPData.caffeinatedType == CaffeinatedProductType.ChewingGum
          && !cautionarySoPData.quantityInPackage
        )
      )
    );
  }

  shouldDisableContinueButton() {
    return this.isInvalidSupplementFactsData || this.exportLabelState.disableDownload || this.exportLabelState.loading;
  }

  downloadLabel(fileType: number) {
    if (this.$store.getters.getUserDetails.isTrial) {
      this.$snotify.info(this.translations.get("toastr_download_label_trial"));
      this.$router.push({ name: "subscription" });
      return;
    }

    this.exportLabelState.disableDownload = true;

    // get the filename
    let filename = this.recipe.name.split(" ").join("_");
    let fileContentType: DownloadNutritionLabelContentTypes;

    fileContentType = this.getFileContentType(fileType);

    if (this.exportLabelState.nutritionCountry && this.exportLabelState.nutritionLabel && this.exportLabelState.primaryLanguage) {
      const additionalLabelInformation: AdditionalLabelInformation = {};

      if (this.exportLabelState.labelTypes[LabelTypeMetadata.FrontOfPack].includeInDownload) {
        additionalLabelInformation[LabelTypeMetadata.FrontOfPack] = {
          frontOfPackageNutritionLabelId: this.exportLabelState.fopNutritionLabelId,
          highInMetadata: this.highIn,
        };
      }
      NutritionLabelRepository.downloadNutritionLabelForRecipe(
        {
          recipeId: this.recipe.id,
          allergens: this.exportLabelState.checkedAllergens,
          nutritionLabelId: this.exportLabelState.nutritionLabel ? this.exportLabelState.nutritionLabel.id : 0,
          primaryLanguage: this.exportLabelState.primaryLanguage ? this.exportLabelState.primaryLanguage.value : "",
          secondaryLanguage: this.exportLabelState.secondaryLanguage ? this.exportLabelState.secondaryLanguage.value : "",
          dietaryTypes: this.exportLabelState.dietary,
          ingredientsIncluded: this.exportLabelState.includeIngredients,
          headerTranslations: this.exportLabelState.headerLabelTranslations,
          additionalContent: this.exportLabelState.mayContain,
          cautionaryMessage: this.exportLabelState.cautionaryMessage,
          containsLessThanPercentOfText: this.exportLabelState.containsLessThanPercentOf,
          nutritionLabelDownloadFileFormat: fileType,
          labelWidth: parseFloat(this.labelWidth()),
          labelHeight: parseFloat(this.labelHeight()),
          freeTextPlaceholders: this.exportLabelState.freeTextPlaceholders,
          ingredientDisplayModels: this.exportLabelState.renderExpandedSubrecipes
            ? this.exportLabelState.ingredientAndSubRecipeDisplaysModel.expandedSubRecipeList
            : this.exportLabelState.ingredientAndSubRecipeDisplaysModel.combinedSubRecipeList,
          labelFormatType: this.packageLabelType,
          additionalLabelInformation,
          nutritionCountry: this.exportLabelState.nutritionCountry.id,
          cautionaryFrontOfPackRenderData: this.exportLabelState.hasValidCautionary ? this.exportLabelState.labelTypes[LabelTypeMetadata.CautionaryFrontOfPack] : null,
          cautionarySideOfPackRenderData: this.exportLabelState.hasValidCautionary ? this.exportLabelState.labelTypes[LabelTypeMetadata.CautionarySideOfPack] : null,
        },
        fileContentType
      )
        .then((response) => {
          this.pushDownloadToClient(response.data, filename, response.headers["content-type"]);
        })
        .catch((error) => {
          let message = "";
          if (error.response) {
            let enc = new TextDecoder("utf-8");
            let data = JSON.parse(enc.decode(error.response.data));
            message = data.errorMessages[0].userVisibleMessage;
          }
          else {
            message = this.translations.get("network_error");
          }
          this.$snotify.error(message);
        });
    }
  }

  labelWidth() {
    let width: any = 0;
    let label = <HTMLImageElement>document.getElementById("labelPreview");
    width = label.naturalWidth;
    return width;
  }

  labelHeight() {
    let height: any = 0;

    let label = <HTMLImageElement>document.getElementById("labelPreview");
    height = label.naturalHeight;

    return height;
  }

  getFileContentType(chosenFileType: number): DownloadNutritionLabelContentTypes {
    if (this.packageLabelType && this.packageLabelType === LabelTypeMetadata.FrontOfPack) {
      return "application/zip";
    }

    return chosenFileType === 0 ? "application/pdf" : "image/jpeg";
  }

  getFileExtension(chosenFileType: DownloadNutritionLabelContentTypes) {
    if (chosenFileType === "application/zip") {
      return ".zip";
    }

    return chosenFileType === "application/pdf" ? '.pdf' : '.jpg';
  }

  pushDownloadToClient(blobData: BlobPart, filename: string, fileContentType: string) {
    // get the blob
    let blob = new Blob([blobData], {
      type: "type: " + fileContentType,
    });

    filename += this.getFileExtension(fileContentType as DownloadNutritionLabelContentTypes);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    link.setAttribute("download", filename);
    link.setAttribute("target", "_blank");
    document.body.appendChild(link);

    let evt = document.createEvent("MouseEvents");
    evt.initMouseEvent(
      "click",
      true,
      true,
      window,
      0,
      0,
      0,
      0,
      0,
      false,
      false,
      false,
      false,
      0,
      null
    );
    link.dispatchEvent(evt);

    this.exportLabelState.disableDownload = false;
    UserRepository.fetchUserInfo().then((response) => {
      this.$store.commit(SET_USER_DETAILS, {
        userDetails: response.data.result,
        logged: true,
      });
    });
  }
}
